import React, { useState, useEffect, useContext } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Chip,
  Avatar,
  IconButton,
  CircularProgress,
  Divider,
} from "@mui/material";
import { Phone, CheckCircle, X, CalendarOff } from "lucide-react";
import { AuthContext } from "../../../../shared/context/auth-context";
import { ClinicaContext } from "../../../../shared/context/clinica-context";
import { useHttpClient } from "../../../../shared/hooks/http-hook";
import ModalConfirmarAgendamento from "../modalConfirmarAgendamento/ModalConfirmarAgendamento";
import { getEstadoLembrete } from "../../../../shared/util/agendamentoProvisorioHelpers";

import "./ModalListaAgendamentosProvisorio.scss";
import { Link } from "react-router-dom";
import ModalCancelarAgendamento from "../modalCancelarAgendamentoProvisorio/ModalCancelarAgendamentoProvisorio";

function ModalListaAgendamentosProvisorio({ open, onClose, onUpdate }) {
  const auth = useContext(AuthContext);
  const clinica = useContext(ClinicaContext);
  const { sendRequest } = useHttpClient();
  const [agendamentos, setAgendamentos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modalGerirOpen, setModalGerirOpen] = useState(false);
  const [modalCancelarOpen, setModalCancelarOpen] = useState(false);
  const [selectedAgendamento, setSelectedAgendamento] = useState(null);

  useEffect(() => {
    if (open && clinica?.clinica?._id) {
      fetchAgendamentos();
    }
  }, [open, clinica?.clinica?._id]);

  const fetchAgendamentos = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      params.append("status", "PENDENTE");

      const responseData = await sendRequest(
        `${process.env.REACT_APP_BACKEND_LINK}/api/agendamentosprovisorio/${
          clinica.clinica._id
        }?${params.toString()}`,
        "GET",
        null,
        {
          Authorization: "Bearer " + auth.token,
        }
      );

      // ✅ ADICIONAR ORDENAÇÃO POR URGÊNCIA
      const agendamentosOrdenados = (responseData.agendamentos || []).sort(
        (a, b) => {
          const estadoA = getEstadoLembrete(a);
          const estadoB = getEstadoLembrete(b);

          const prioridade = { ATRASADO: 0, HOJE: 1, FUTURO: 2, OK: 3 };

          if (prioridade[estadoA.estado] !== prioridade[estadoB.estado]) {
            return prioridade[estadoA.estado] - prioridade[estadoB.estado];
          }

          // Se mesma prioridade, mais atrasado primeiro
          if (
            estadoA.diasPassados !== undefined &&
            estadoB.diasPassados !== undefined
          ) {
            return estadoB.diasPassados - estadoA.diasPassados;
          }

          return 0;
        }
      );

      setAgendamentos(agendamentosOrdenados);
      // ✅ FIM DA MODIFICAÇÃO
    } catch (err) {
      console.error("Erro ao buscar agendamentos:", err);
    } finally {
      setLoading(false);
    }
  };

  const handleRegistrarChamada = async (agendamentoId, tipoChamada) => {
    try {
      await sendRequest(
        `${process.env.REACT_APP_BACKEND_LINK}/api/agendamentosprovisorio/chamada/${agendamentoId}`,
        "PATCH",
        JSON.stringify({ tipoChamada }),
        {
          "Content-Type": "application/json",
          Authorization: "Bearer " + auth.token,
        }
      );

      fetchAgendamentos();
      onUpdate?.();
    } catch (err) {
      console.error("Erro ao registrar chamada:", err);
    }
  };

  const handleCancelarAgendamento = async (motivo, observacoes) => {
    try {
      await sendRequest(
        `${process.env.REACT_APP_BACKEND_LINK}/api/agendamentosprovisorio/cancelar/${selectedAgendamento.id}`,
        "PATCH",
        JSON.stringify({ motivo, observacoes }),
        {
          "Content-Type": "application/json",
          Authorization: "Bearer " + auth.token,
        }
      );

      fetchAgendamentos();
      onUpdate?.();
    } catch (err) {
      console.error("Erro ao cancelar agendamento:", err);
      throw err;
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case "PENDENTE":
        return "warning";
      case "CONFIRMADO":
        return "success";
      default:
        return "default";
    }
  };

  const formatDate = (date) => {
    return new Date(date).toLocaleDateString("pt-PT", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
    });
  };

  const getProximoLembrete = (agendamento) => {
    const agora = new Date();

    if (
      !agendamento.lembrete1MesEnviado &&
      new Date(agendamento.dataLembrete1Mes) <= agora
    ) {
      return "1 mês";
    }
    if (
      !agendamento.lembrete1SemanaEnviado &&
      new Date(agendamento.dataLembrete1Semana) <= agora
    ) {
      return "1 semana";
    }
    if (
      !agendamento.lembrete1DiaEnviado &&
      new Date(agendamento.dataLembrete1Dia) <= agora
    ) {
      return "1 dia";
    }
    return null;
  };

  const handleCloseGerir = () => {
    setModalGerirOpen(false);
    setSelectedAgendamento(null);
    fetchAgendamentos();
    onUpdate?.();
  };

  return (
    <>
      <Dialog
        open={open}
        onClose={onClose}
        maxWidth="md"
        fullWidth
        className="modal-lista-agendamentos"
      >
        <DialogTitle>
          <div className="modal-title-container">
            <span>Agendamentos Provisórios - Lembretes</span>
            <IconButton onClick={onClose} size="small">
              <X size={20} />
            </IconButton>
          </div>
        </DialogTitle>

        <DialogContent>
          {loading ? (
            <div className="loading-container">
              <CircularProgress />
            </div>
          ) : agendamentos.length === 0 ? (
            <div className="empty-state">
              <p>Não há agendamentos provisórios pendentes.</p>
            </div>
          ) : (
            <div className="agendamentos-list">
              {agendamentos.map((agendamento, index) => {
                const proximoLembrete = getProximoLembrete(agendamento);
                const estadoLembrete = getEstadoLembrete(agendamento); // ✅ ADICIONAR

                return (
                  <React.Fragment key={agendamento.id}>
                    {/* ✅ MODIFICAR ESTRUTURA COMPLETA */}
                    <div
                      className="agendamento-item"
                      style={{
                        borderLeft: `4px solid ${estadoLembrete.cor}`,
                        paddingLeft: "12px",
                      }}
                    >
                      <div className="agendamento-info">
                        <Avatar
                          src={
                            agendamento.cliente.foto
                              ? `${process.env.REACT_APP_BACKEND_LINK}/${agendamento.cliente.foto}`
                              : "/images/fallBackPicture.png"
                          }
                          alt={agendamento.cliente.nome}
                          className="cliente-avatar"
                        />
                        <div className="agendamento-details">
                          <strong className="cliente-nome">
                            <Link
                              to={`/cliente/${agendamento.cliente._id}`}
                              className="linkNomeCliente"
                            >
                              {agendamento.cliente.nome}
                            </Link>
                          </strong>
                          <span className="agendamento-date">
                            Data: {formatDate(agendamento.data_inicio_execucao)}
                          </span>

                          {/* ✅ ADICIONAR MENSAGEM DE URGÊNCIA */}
                          {estadoLembrete.mensagem && (
                            <span
                              className="lembrete-urgencia"
                              style={{
                                color: estadoLembrete.cor,
                                fontSize: "13px",
                                fontWeight: "500",
                              }}
                            >
                              📞 {estadoLembrete.mensagem}
                            </span>
                          )}

                          <div className="chips-container">
                            {proximoLembrete && (
                              <Chip
                                label={`Lembrete: ${proximoLembrete}`}
                                size="small"
                                color="warning"
                                icon={<Phone size={16} />}
                              />
                            )}
                            <Chip
                              label={agendamento.status}
                              size="small"
                              color={getStatusColor(agendamento.status)}
                            />
                          </div>
                        </div>
                      </div>

                      <div className="agendamento-actions">
                        {agendamento.status !== "CONFIRMADO" && (
                          <>
                            {!agendamento.chamadas?.primeira && (
                              <Button
                                size="small"
                                variant="outlined"
                                startIcon={<Phone size={16} />}
                                onClick={() =>
                                  handleRegistrarChamada(
                                    agendamento.id,
                                    "PRIMEIRA"
                                  )
                                }
                              >
                                1ª Chamada
                              </Button>
                            )}

                            {agendamento.chamadas?.primeira &&
                              !agendamento.chamadas?.segunda && (
                                <Button
                                  size="small"
                                  variant="outlined"
                                  startIcon={<Phone size={16} />}
                                  onClick={() =>
                                    handleRegistrarChamada(
                                      agendamento.id,
                                      "SEGUNDA"
                                    )
                                  }
                                >
                                  2ª Chamada
                                </Button>
                              )}
                          </>
                        )}

                        {agendamento.status === "PENDENTE" && (
                          <>
                            <Button
                              size="small"
                              variant="contained"
                              color="success"
                              style={{ padding: "5px" }}
                              startIcon={<CheckCircle size={20} />}
                              onClick={() => {
                                setSelectedAgendamento(agendamento);
                                setModalGerirOpen(true);
                              }}
                            ></Button>

                            <Button
                              size="small"
                              variant="contained"
                              color="error"
                              style={{ padding: "5px" }}
                              startIcon={<CalendarOff size={20} />}
                              onClick={() => {
                                setSelectedAgendamento(agendamento);
                                setModalCancelarOpen(true);
                              }}
                            ></Button>
                          </>
                        )}
                      </div>
                    </div>
                    {index < agendamentos.length - 1 && <Divider />}
                  </React.Fragment>
                );
              })}
            </div>
          )}
        </DialogContent>

        <DialogActions>
          <Button onClick={onClose}>Fechar</Button>
        </DialogActions>
      </Dialog>

      {modalGerirOpen && selectedAgendamento && (
        <ModalConfirmarAgendamento
          open={modalGerirOpen}
          onClose={handleCloseGerir}
          agendamento={selectedAgendamento}
          onUpdate={handleCloseGerir}
        />
      )}

      {modalCancelarOpen && selectedAgendamento && (
        <ModalCancelarAgendamento
          open={modalCancelarOpen}
          onClose={() => setModalCancelarOpen(false)}
          agendamento={selectedAgendamento}
          onConfirm={handleCancelarAgendamento}
        />
      )}
    </>
  );
}

export default ModalListaAgendamentosProvisorio;
